import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';

export default function APITestDiagnosticsPage() {
  const { user, loading: authLoading } = useAuth();

  if (authLoading) return <div className="p-8 text-center">Loading...</div>;
  if (!user || user.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">This page is restricted to administrators only.</p>
      </div>
    );
  }
  const [results, setResults] = useState<any>({});
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      runTests();
    }
  }, [user, authLoading]);

  const runTests = async () => {
    setTestLoading(true);
    const newResults: any = {};

    console.log('🚀 Starting API Diagnostic Tests...\n');

    // Test 1: Check auth status
    console.log('📌 TEST 1: Auth Status');
    newResults.auth = {
      userEmail: user?.email,
      hasToken: !!localStorage.getItem('token'),
      token: localStorage.getItem('token') ? `${localStorage.getItem('token')?.substring(0, 20)}...` : 'NO TOKEN'
    };
    console.log('✅ Auth Status:', newResults.auth);

    // Test 2: Test GET /customer/service-requests
    console.log('\n📌 TEST 2: Fetch Service Requests');
    try {
      const response = await apiService.request('/customer/service-requests', 'GET');
      newResults.serviceRequests = {
        success: response.success,
        status: 'success',
        dataCount: Array.isArray(response.data) ? response.data.length : 0,
        data: response.data
      };
      console.log('✅ Service Requests:', newResults.serviceRequests);
    } catch (error: any) {
      newResults.serviceRequests = {
        success: false,
        status: 'error',
        error: error.message,
        details: error?.response?.data
      };
      console.error('❌ Service Requests Error:', newResults.serviceRequests);
    }

    // Test 3: Test POST endpoint
    console.log('\n📌 TEST 3: Test Quote Submission');
    try {
      const testPayload = {
        service_category: 'cleaning',
        property_type: 'residential',
        service_type: 'deep-clean',
        contact_email: user?.email,
        contact_phone: '+1 555-0123',
        contact_name: user?.first_name || 'Test User',
        company: 'Test Company',
        bedrooms: 2,
        bathrooms: 1
      };

      newResults.testSubmission = {
        payload: testPayload,
        status: 'ready_to_test'
      };
      console.log('✅ Test Submission Ready:', newResults.testSubmission);
    } catch (error: any) {
      console.error('❌ Test Submission Error:', error);
    }

    setResults(newResults);
    setTestLoading(false);
    console.log('\n✨ Tests Complete!\n');
  };

  if (authLoading) {
    return <div className="p-8 text-center">Loading auth...</div>;
  }

  if (!user) {
    return <div className="p-8 text-center text-red-600">Please log in first</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">API Diagnostic Test</h1>
          <p className="text-gray-600">Test and debug the service request API</p>
        </div>

        <div className="grid gap-6 mb-8">
          {/* Auth Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Authentication Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div><strong>Email:</strong> {results.auth?.userEmail || 'N/A'}</div>
                <div><strong>Token:</strong> <code className="bg-gray-100 px-2 py-1 rounded">{results.auth?.token || 'NO TOKEN'}</code></div>
                <div><strong>Has Token:</strong> {results.auth?.hasToken ? '✅ Yes' : '❌ No'}</div>
              </div>
            </CardContent>
          </Card>

          {/* Service Requests Test */}
          {results.serviceRequests && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {results.serviceRequests.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  GET /customer/service-requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2 items-center">
                    <Badge variant={results.serviceRequests.success ? 'default' : 'destructive'}>
                      {results.serviceRequests.status}
                    </Badge>
                    {results.serviceRequests.success && (
                      <span className="text-green-600 font-semibold">
                        ✅ {results.serviceRequests.dataCount} request(s) found
                      </span>
                    )}
                  </div>

                  {results.serviceRequests.success && results.serviceRequests.data && (
                    <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-auto">
                      <p className="font-mono text-sm">
                        <pre>{JSON.stringify(results.serviceRequests.data, null, 2)}</pre>
                      </p>
                    </div>
                  )}

                  {!results.serviceRequests.success && (
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <p className="text-red-700 font-mono text-sm">
                        Error: {results.serviceRequests.error}
                      </p>
                      {results.serviceRequests.details && (
                        <pre className="text-xs mt-2">{JSON.stringify(results.serviceRequests.details, null, 2)}</pre>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Browser Console Help */}
          <Card>
            <CardHeader>
              <CardTitle>📊 What to Check in Browser Console</CardTitle>
              <CardDescription>Open DevTools (F12) and look for these messages</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="font-mono text-sm text-blue-900">
                  Look for messages starting with:<br/>
                  • <strong>[REQUEST SERVICE]</strong> - Form submission debug logs<br/>
                  • <strong>[DASHBOARD]</strong> - Dashboard data fetch logs<br/>
                  • <strong>📡, ✅, ❌</strong> - Test result indicators
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-bold">Steps to Debug:</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Open DevTools (F12) → Console tab</li>
                  <li>Filter by messages with <strong>[DASHBOARD]</strong> or <strong>[REQUEST SERVICE]</strong></li>
                  <li>Check if API responses are success or showing errors</li>
                  <li>Look for the data being returned</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-4">
          <Button onClick={runTests} disabled={testLoading} className="gap-2">
            {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Run Tests Again
          </Button>
          <Button variant="outline" onClick={() => window.location.href = '/dashboard'}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
